import React, { useMemo } from 'react';
import { MessageType, TopicType } from '@/types/assistant';
import { cn } from '@/lib/utils';
import { motion } from 'framer-motion';
import { MessageAvatar } from './MessageAvatar';
import { MessageHeader } from './MessageHeader';
import { MessageContent } from './MessageContent';
import { ContextualQuickActions } from './ContextualQuickActions';
import { useIsMobile } from '@/hooks/use-mobile';

interface ChatMessageProps {
  message: MessageType;
  topic: TopicType;
  isLatest: boolean;
  onSuggestionClick?: (suggestion: string) => void;
  onFeedbackChange?: (messageId: string, feedback: 'positive' | 'negative' | null) => void;
  onRegenerateMessage?: (messageId: string) => void;
}

export const ChatMessage: React.FC<ChatMessageProps> = React.memo(({ 
  message, 
  topic,
  isLatest,
  onSuggestionClick,
  onFeedbackChange,
  onRegenerateMessage
}) => {
  const isAi = message.role === 'assistant';
  const isMobile = useIsMobile();
  
  const isStreaming = Boolean(message.is_streaming);
  
  const displayContent = useMemo(() => {
    if (isStreaming) {
      return message.stream_content || '';
    }
    return message.content || message.stream_content || '';
  }, [isStreaming, message.stream_content, message.content]);
  
  const messageTimestamp = new Date(message.stream_timestamp || message.timestamp).toLocaleString('en-US', {
    hour: 'numeric',
    minute: 'numeric',
    hour12: true
  });

  const suggestions = Array.isArray(message.suggestions) ? message.suggestions : [];

  const containerAnimations = useMemo(() => ({
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { 
      duration: 0.3, 
      ease: [0.25, 0.25, 0, 1],
      type: "spring",
      stiffness: 300,
      damping: 30
    }
  }), []);

  if (!isAi && !displayContent) {
    return null;
  }

  if (isAi) {
    // AI messages: vertical layout with avatar on top, full-width bubble below
    return (
      <motion.div 
        className={cn(
          "flex flex-col my-8 group relative w-full",
          isMobile && "my-6"
        )}
        {...containerAnimations}
      >
        {/* Avatar and header row */}
        <div className="flex items-start gap-4 mb-2">
          <MessageAvatar 
            isAi={isAi} 
            topic={topic} 
            isStreaming={isStreaming} 
          />
          
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between">
              <MessageHeader 
                isAi={isAi}
                topic={topic}
                isStreaming={isStreaming}
                timestamp={messageTimestamp}
              />
              
              {/* Contextual quick actions */}
              {!isStreaming && (
                <ContextualQuickActions
                  messageId={message.id}
                  messageContent={displayContent}
                  topic={topic}
                  onRegenerateMessage={onRegenerateMessage}
                />
              )}
            </div>
          </div>
        </div>
        
        {/* Full-width content below avatar */}
        <div className="w-full">
          <div className="prose prose-sm dark:prose-invert max-w-none">
            <MessageContent
              isAi={isAi}
              displayContent={displayContent}
              isStreaming={isStreaming}
              topic={topic}
              isLatest={isLatest}
              suggestions={suggestions}
              messageId={message.id}
              feedback={message.feedback}
              onSuggestionClick={onSuggestionClick}
              onFeedbackChange={onFeedbackChange}
            />
          </div>
        </div>
      </motion.div>
    );
  }

  // User messages: keep original horizontal layout
  return (
    <motion.div 
      className={cn(
        "flex items-start gap-4 my-8 group relative flex-row-reverse",
        isMobile && "gap-3 my-6"
      )}
      {...containerAnimations}
    >
      <MessageAvatar 
        isAi={isAi} 
        topic={topic} 
        isStreaming={isStreaming} 
      />
      
      <div className={cn(
        "flex-1 space-y-1 min-w-0 flex flex-col items-end"
      )}>
        <div className="flex items-center justify-between">
          <MessageHeader 
            isAi={isAi}
            topic={topic}
            isStreaming={isStreaming}
            timestamp={messageTimestamp}
          />
        </div>
        
        <div className={cn(
          "prose prose-sm dark:prose-invert max-w-none text-right"
        )}>
          <MessageContent
            isAi={isAi}
            displayContent={displayContent}
            isStreaming={isStreaming}
            topic={topic}
            isLatest={isLatest}
            suggestions={suggestions}
            messageId={message.id}
            feedback={message.feedback}
            onSuggestionClick={onSuggestionClick}
            onFeedbackChange={onFeedbackChange}
          />
        </div>
      </div>
    </motion.div>
  );
}, (prevProps, nextProps) => {
  const prevMessage = prevProps.message;
  const nextMessage = nextProps.message;
  
  if (nextMessage.is_streaming || prevMessage.is_streaming) {
    return false;
  }
  
  if (prevMessage.stream_content !== nextMessage.stream_content ||
      prevMessage.content !== nextMessage.content) {
    return false;
  }
  
  return (
    prevMessage.id === nextMessage.id &&
    prevProps.topic === nextProps.topic &&
    prevProps.isLatest === nextProps.isLatest
  );
});

ChatMessage.displayName = "ChatMessage";
